// 회의 녹음 임시 보관(IndexedDB) + 생존 판정(Web Locks).
// 🔴 녹음하는 탭이 그 녹음의 Web Lock 을 쥔다 = 그 레코드의 주인이다. 탭이 죽으면
//    (새로고침·401·탭 닫기·크래시) 브라우저가 잠금을 풀어 주고, 잠금이 풀린 레코드가
//    «주인 없는 녹음»이다. state 는 문구용 라벨일 뿐 생존 판정에 쓰지 않는다.
// 🔴 전역 식별자 indexedDB·crypto 를 window. 없이 참조하지 않는다(jsdom 에서 ReferenceError).

export const REC_DB_NAME = 'aigw-recording';
export const REC_DB_VERSION = 1;
export const REC_SESSION_STORE = 'rec_sessions';
export const REC_CHUNK_STORE = 'rec_chunks';
export const REC_LOCK_PREFIX = 'aigw-rec:';
export const FOREIGN_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
export const REC_TIMESLICE_SEC = 1;
const OPEN_TIMEOUT_MS = 3000;

let dbPromise = null;
let disabled = false;   // 이 페이지에서 DB 를 못 연 적이 있으면 기능을 끈다

export function isRecordingPersistSupported() {
  if (disabled) return false;
  if (typeof window === 'undefined') return false;
  return window.isSecureContext === true
    && !!window.indexedDB
    && typeof window.navigator?.locks?.request === 'function'
    && typeof window.crypto?.randomUUID === 'function';
}

export function newPersistId() {
  return window.crypto.randomUUID();
}

// 조각 키는 [persistId, seq] 배열이라 persistId 하나로 범위를 잡을 수 있다.
function chunkRange(persistId) {
  return window.IDBKeyRange.bound([persistId, 0], [persistId, Number.MAX_SAFE_INTEGER]);
}

function requestDone(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('indexeddb request failed'));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error || new Error('indexeddb transaction failed'));
    tx.onabort = () => reject(tx.error || new Error('indexeddb transaction aborted'));
  });
}

function openDbInternal() {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (db) => {
      if (settled) return;
      settled = true;
      // 🔴 못 열면 이 페이지에서는 아예 끈다 — 녹음이 이걸 기다리는 일은 없어야 한다.
      if (!db) disabled = true;
      resolve(db || null);
    };
    let request = null;
    try {
      request = window.indexedDB.open(REC_DB_NAME, REC_DB_VERSION);
    } catch (e) {
      finish(null);
      return;
    }
    const timer = setTimeout(() => finish(null), OPEN_TIMEOUT_MS);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(REC_SESSION_STORE)) {
        db.createObjectStore(REC_SESSION_STORE, { keyPath: 'persistId' });
      }
      if (!db.objectStoreNames.contains(REC_CHUNK_STORE)) {
        db.createObjectStore(REC_CHUNK_STORE);
      }
    };
    request.onsuccess = () => {
      clearTimeout(timer);
      const db = request.result;
      db.onversionchange = () => { try { db.close(); } catch (e) { /* ignore */ } dbPromise = null; };
      finish(db);
    };
    request.onerror = () => { clearTimeout(timer); finish(null); };
    request.onblocked = () => { clearTimeout(timer); finish(null); };
  });
}

async function openDb() {
  if (!isRecordingPersistSupported()) return null;
  if (!dbPromise) dbPromise = openDbInternal();
  const db = await dbPromise;
  if (!db) dbPromise = null;
  return db;
}

// 🔴 잠금 해제 함수는 콜백 **안에서** 바깥 Promise 의 resolve 로 넘긴다.
//    navigator.locks.request 가 돌려주는 Promise 는 콜백의 Promise 가 끝나야 resolve 되므로,
//    그걸 await 해서 해제 함수를 얻으려 하면 영원히 기다린다(기능이 조용히 죽는다).
export function acquireRecLock(persistId) {
  if (!isRecordingPersistSupported() || !persistId) return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value) => { if (!settled) { settled = true; resolve(value); } };
    try {
      window.navigator.locks.request(REC_LOCK_PREFIX + persistId, { ifAvailable: true }, (lock) => {
        if (!lock) { settle(null); return undefined; }
        return new Promise((release) => { settle(() => release()); });
      }).catch(() => settle(null));
    } catch (e) {
      settle(null);
    }
  });
}

export async function putSession(row) {
  const db = await openDb();
  if (!db || !row?.persistId) return false;
  const tx = db.transaction([REC_SESSION_STORE], 'readwrite');
  tx.objectStore(REC_SESSION_STORE).put({ ...row, updatedAt: row.updatedAt || Date.now() });
  await txDone(tx);
  return true;
}

export async function patchSession(persistId, patch) {
  const db = await openDb();
  if (!db || !persistId) return false;
  const tx = db.transaction([REC_SESSION_STORE], 'readwrite');
  const store = tx.objectStore(REC_SESSION_STORE);
  const current = await requestDone(store.get(persistId));
  if (current) store.put({ ...current, ...patch, updatedAt: Date.now() });
  await txDone(tx);
  return !!current;
}

// 🔴 세션 행과 **같은 트랜잭션**에서 확인한다 — 이미 지워진 녹음의 조각이 고아로 남지 않게.
export async function putChunk(persistId, seq, blob) {
  const db = await openDb();
  if (!db || !persistId) return false;
  const tx = db.transaction([REC_SESSION_STORE, REC_CHUNK_STORE], 'readwrite');
  const row = await requestDone(tx.objectStore(REC_SESSION_STORE).get(persistId));
  if (row) tx.objectStore(REC_CHUNK_STORE).put(blob, [persistId, seq]);
  await txDone(tx);
  return !!row;
}

export async function listSessions() {
  const db = await openDb();
  if (!db) return [];
  const tx = db.transaction([REC_SESSION_STORE], 'readonly');
  const rows = await requestDone(tx.objectStore(REC_SESSION_STORE).getAll());
  return (rows || []).slice().sort((a, b) => (Number(a?.startedAt) || 0) - (Number(b?.startedAt) || 0));
}

export async function countChunks(persistId) {
  const db = await openDb();
  if (!db || !persistId) return 0;
  const tx = db.transaction([REC_CHUNK_STORE], 'readonly');
  const total = await requestDone(tx.objectStore(REC_CHUNK_STORE).count(chunkRange(persistId)));
  return Number(total) || 0;
}

export async function readChunks(persistId) {
  const db = await openDb();
  if (!db || !persistId) return [];
  const tx = db.transaction([REC_CHUNK_STORE], 'readonly');
  const store = tx.objectStore(REC_CHUNK_STORE);
  const range = chunkRange(persistId);
  const keysRequest = store.getAllKeys(range);
  const valuesRequest = store.getAll(range);
  const keys = await requestDone(keysRequest);
  const values = await requestDone(valuesRequest);
  return (keys || [])
    .map((key, i) => ({ seq: Array.isArray(key) ? Number(key[1]) || 0 : i, blob: (values || [])[i] }))
    .filter((item) => !!item.blob)
    .sort((a, b) => a.seq - b.seq)
    .map((item) => item.blob);
}

// readChunks 와 같은 읽기지만 조각 번호(seq)를 같이 돌려준다 — 이어 녹음 구간을 나누는 데 쓴다.
export async function readChunkEntries(persistId) {
  const db = await openDb();
  if (!db || !persistId) return [];
  const tx = db.transaction([REC_CHUNK_STORE], 'readonly');
  const store = tx.objectStore(REC_CHUNK_STORE);
  const range = chunkRange(persistId);
  const keysRequest = store.getAllKeys(range);
  const valuesRequest = store.getAll(range);
  const keys = await requestDone(keysRequest);
  const values = await requestDone(valuesRequest);
  return (keys || [])
    .map((key, i) => ({ seq: Array.isArray(key) ? Number(key[1]) || 0 : i, blob: (values || [])[i] }))
    .filter((item) => !!item.blob)
    .sort((a, b) => a.seq - b.seq);
}

// 마지막 조각 번호(없으면 -1). 이어 녹음 구간은 그다음 번호(seqBase)부터 쌓는다.
export async function lastSeq(persistId) {
  const db = await openDb();
  if (!db || !persistId) return -1;
  const tx = db.transaction([REC_CHUNK_STORE], 'readonly');
  const keys = await requestDone(tx.objectStore(REC_CHUNK_STORE).getAllKeys(chunkRange(persistId)));
  let last = -1;
  (keys || []).forEach((key) => {
    const seq = Array.isArray(key) ? Number(key[1]) : NaN;
    if (Number.isFinite(seq) && seq > last) last = seq;
  });
  return last;
}

// 세션 행의 구간 경계 `segments: [{startSeq, mime}]`. 없으면(옛 행) «구간 하나, startSeq 0, row.mime».
// 같은 startSeq 가 여럿이면 마지막 것 하나만 쓴다(경계를 적은 뒤 조각 0개로 탭이 죽으면
// 다음 이어 녹음이 같은 startSeq 를 또 적는다).
function normalizeSegments(row) {
  const fallbackMime = row?.mime || 'audio/webm';
  const byStart = new Map();
  (Array.isArray(row?.segments) ? row.segments : []).forEach((seg) => {
    const startSeq = Number(seg?.startSeq);
    if (!Number.isFinite(startSeq) || startSeq < 0) return;
    byStart.delete(startSeq);
    byStart.set(startSeq, { startSeq, mime: seg?.mime || fallbackMime });
  });
  const list = [...byStart.values()].sort((a, b) => a.startSeq - b.startSeq);
  if (!list.length || list[0].startSeq > 0) list.unshift({ startSeq: 0, mime: fallbackMime });
  return list;
}

// 조각을 구간별로 나눈다 → [{startSeq, mime, blobs}] (순수 함수). 🔴 조각 0개 구간은 버린다.
export function splitSegments(row, entries) {
  const bounds = normalizeSegments(row);
  const out = bounds.map((seg) => ({ startSeq: seg.startSeq, mime: seg.mime, blobs: [] }));
  (Array.isArray(entries) ? entries : [])
    .filter((item) => item && item.blob && Number.isFinite(Number(item.seq)))
    .sort((a, b) => Number(a.seq) - Number(b.seq))
    .forEach((item) => {
      const seq = Number(item.seq);
      let idx = 0;
      for (let i = 0; i < out.length; i += 1) {
        if (out[i].startSeq <= seq) idx = i;
      }
      out[idx].blobs.push(item.blob);
    });
  return out.filter((seg) => seg.blobs.length > 0);
}

// 🔴 이어 녹음 구간만 버린다 — 조각 seq ≥ seqBase 삭제 + startSeq === seqBase 인 경계 제거 +
//    메모·제목 되돌리기를 **한 readwrite 트랜잭션(두 스토어)** 에서 한다. 두 쓰기가 갈라지면 남은 조각이
//    이전 구간에 빨려 들어가 앞 녹음이 소리 없이 사라진다. 그 경계가 없으면(경계 기록 실패) 이전
//    구간 경계는 건드리지 않는다.
export async function discardTailSegment(persistId, seqBase, restore = {}) {
  const base = Number(seqBase);
  if (!persistId || !Number.isFinite(base) || base <= 0) return false;
  const db = await openDb();
  if (!db) return false;
  const tx = db.transaction([REC_SESSION_STORE, REC_CHUNK_STORE], 'readwrite');
  const sessions = tx.objectStore(REC_SESSION_STORE);
  tx.objectStore(REC_CHUNK_STORE).delete(
    window.IDBKeyRange.bound([persistId, base], [persistId, Number.MAX_SAFE_INTEGER])
  );
  const row = await requestDone(sessions.get(persistId));
  if (row) {
    const next = { ...row, updatedAt: Date.now() };
    if (Array.isArray(row.segments)) next.segments = row.segments.filter((seg) => Number(seg?.startSeq) !== base);
    if (Array.isArray(restore?.notes)) next.notes = [...restore.notes];
    if (typeof restore?.title === 'string') next.title = restore.title;
    if (typeof restore?.state === 'string') next.state = restore.state;
    sessions.put(next);
  }
  await txDone(tx);
  return !!row;
}

// 🔴 세션 행과 조각을 **한 트랜잭션**에서 지운다(반쪽만 지워진 상태를 만들지 않는다).
export async function deleteRecording(persistId) {
  const db = await openDb();
  if (!db || !persistId) return false;
  const tx = db.transaction([REC_SESSION_STORE, REC_CHUNK_STORE], 'readwrite');
  tx.objectStore(REC_SESSION_STORE).delete(persistId);
  tx.objectStore(REC_CHUNK_STORE).delete(chunkRange(persistId));
  await txDone(tx);
  return true;
}

// finalizeRecSession 의 확장자 규칙과 같다.
export function extForMime(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('mp4')) return 'm4a';
  if (t.includes('aac')) return 'aac';
  if (t.includes('ogg')) return 'ogg';
  return 'webm';
}

// 부분 webm 은 컨테이너에 길이가 없다(<audio> duration = Infinity) → 조각 수로 추정한다.
export function estimateDurationSec(chunkCount) {
  return Math.max(0, Number(chunkCount) || 0) * REC_TIMESLICE_SEC;
}

export function isForeignExpired(row, now = Date.now()) {
  const startedAt = Number(row?.startedAt);
  if (!Number.isFinite(startedAt) || startedAt <= 0) return false;
  return now - startedAt > FOREIGN_RETENTION_MS;
}
